import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { User } from 'firebase/auth';
import { firebaseAuth, isFirebaseEnabled, onIdTokenChanged, signOut as fbSignOut } from './firebase';

interface AuthState {
  user: User | null;
  loading: boolean;
  /** Latest ID token; refreshed automatically. Null if not signed in. */
  token: string | null;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthState>({ user: null, loading: true, token: null, signOut: async () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isFirebaseEnabled || !firebaseAuth) {
      setLoading(false);
      return;
    }
    const unsub = onIdTokenChanged(firebaseAuth, async (u) => {
      setUser(u);
      if (u) {
        const t = await u.getIdToken();
        setToken(t);
        localStorage.setItem('access_token', t);
      } else {
        setToken(null);
        localStorage.removeItem('access_token');
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  return (
    <Ctx.Provider value={{ user, loading, token, signOut: () => fbSignOut() }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() { return useContext(Ctx); }
